import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('wakeDetails');
export interface WakeDetailsData {
  id?: string;
  wakeType?: string;
  wakeLocationPreference?: boolean;
  wakeLocation?: string;
  casketDisplayPreference?: boolean;
  casketDisplay?: string;
  shareMemories?: boolean;
  favoriteOutfitPreference?: boolean;
  favoriteOutfit?: string;
  favoritePhotosPreference?: boolean;
  favoritePhotos?: string;
  favoriteArtPreference?: boolean;
  favoriteArt?: string;
  favoriteVideosPreference?: boolean;
  favoriteVideos?: string;
  readingsPreference?: boolean;
  readingsName?: string;
  specialRequests?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateWakeDetailsRequest {
  wakeType?: string;
  wakeLocationPreference?: boolean;
  wakeLocation?: string;
  casketDisplayPreference?: boolean;
  casketDisplay?: string;
  shareMemories?: boolean;
  favoriteOutfitPreference?: boolean;
  favoriteOutfit?: string;
  favoritePhotosPreference?: boolean;
  favoritePhotos?: string;
  favoriteArtPreference?: boolean;
  favoriteArt?: string;
  favoriteVideosPreference?: boolean;
  favoriteVideos?: string;
  readingsPreference?: boolean;
  readingsName?: string;
  specialRequests?: string;
}

export interface UpdateWakeDetailsRequest {
  wakeType?: string;
  wakeLocationPreference?: boolean;
  wakeLocation?: string;
  casketDisplayPreference?: boolean;
  casketDisplay?: string;
  shareMemories?: boolean;
  favoriteOutfitPreference?: boolean;
  favoriteOutfit?: string;
  favoritePhotosPreference?: boolean;
  favoritePhotos?: string;
  favoriteArtPreference?: boolean;
  favoriteArt?: string;
  favoriteVideosPreference?: boolean;
  favoriteVideos?: string;
  readingsPreference?: boolean;
  readingsName?: string;
  specialRequests?: string;
}

export const wakeDetailsService = {
  createWakeDetails: async (data: CreateWakeDetailsRequest): Promise<WakeDetailsData> => {
    try {
      logger.debug('[Service] Creating wake details with data:', data);
      const response = await http.post('/api/wake-details', data);
      logger.debug('[Service] Create response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error creating wake details:', error);
      throw error;
    }
  },

  updateWakeDetails: async (id: string, data: UpdateWakeDetailsRequest): Promise<WakeDetailsData> => {
    try {
      logger.debug('[Service] Updating wake details with ID:', id, 'data:', data);
      const response = await http.patch(`/api/wake-details/${id}`, data);
      logger.debug('[Service] Update response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error updating wake details:', error);
      throw error;
    }
  },

  getWakeDetails: async (): Promise<WakeDetailsData[]> => {
    try {
      logger.debug('[Service] Getting all wake details');
      const response = await http.get('/api/wake-details');
      logger.debug('[Service] Get all response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting wake details:', error);
      throw error;
    }
  },

  getWakeDetailsById: async (id: string): Promise<WakeDetailsData> => {
    try {
      logger.debug('[Service] Getting wake details by ID:', id);
      const response = await http.get(`/api/wake-details/${id}`);
      logger.debug('[Service] Get by ID response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting wake details by ID:', error);
      throw error;
    }
  },

  deleteWakeDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Deleting wake details with ID:', id);
      await http.delete(`/api/wake-details/${id}`);
      logger.debug('[Service] Delete completed');
    } catch (error: any) {
      logger.error('[Service] Error deleting wake details:', error);
      throw error;
    }
  },

  lockWakeDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Locking wake details with ID:', id);
      await http.post(`/api/wake-details/${id}/lock`);
      logger.debug('[Service] Lock completed');
    } catch (error: any) {
      logger.error('[Service] Error locking wake details:', error);
      throw error;
    }
  },

  unlockWakeDetails: async (id: string, granteeEmails: string[]): Promise<void> => {
    try {
      logger.debug('[Service] Unlocking wake details with ID:', id, 'for emails:', granteeEmails);
      await http.post(`/api/wake-details/${id}/unlock`, { granteeEmails });
      logger.debug('[Service] Unlock completed');
    } catch (error: any) {
      logger.error('[Service] Error unlocking wake details:', error);
      throw error;
    }
  },

  getWakeDetailsAccessGrants: async (id: string): Promise<any[]> => {
    try {
      logger.debug('[Service] Getting access grants for wake details with ID:', id);
      const response = await http.get(`/api/wake-details/${id}/access-grants`);
      logger.debug('[Service] Access grants response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting wake details access grants:', error);
      throw error;
    }
  },
};